/**
 * Quotations — §7. The list plus a detail drawer, rather than a separate
 * record page: approving a quote means reading its lines and deciding, and
 * bouncing between two routes to do that turns a two-minute review into a
 * navigation exercise.
 *
 * Revisions are separate records sharing a `root_quotation_id`, so the same
 * number can appear more than once with a different `version_no` — the badge
 * is part of the identity, not decoration.
 */
import { useMemo, useState } from 'react';
import { useCursorPages } from '../../hooks/useCursorPages.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useAuth } from '../../hooks/useAuth.js';
import { DataTable } from '../../components/common/DataTable.jsx';
import {
  PageHeader, Card, Badge, SearchInput, ChipSelect, Drawer, Spinner,
} from '../../components/common/index.jsx';
import { money, date, percent } from '../../utils/formatters.js';
import {
  useQuotations, useQuotation, useQuotationVersions, useCreateQuotation, useUpdateQuotation,
  useDeleteQuotation, useReviseQuotation, useSubmitForApproval, useSendQuotation, useDecideApproval,
  useQuotationOptions,
} from './useQuotations.js';
import {
  QuotationFormModal, DeleteQuotationModal, ReviseQuotationModal, SendQuotationModal, DecideApprovalModal,
} from './QuotationModals.jsx';

const STATUS_TONE = {
  draft: 'neutral', pending_approval: 'amber', approved: 'teal', rejected: 'rose',
  sent: 'sky', viewed: 'sky', accepted: 'emerald', declined: 'rose', expired: 'neutral', revised: 'neutral',
};
const STATUS_LABEL = {
  draft: 'Draft', pending_approval: 'Pending approval', approved: 'Approved', rejected: 'Rejected',
  sent: 'Sent', viewed: 'Viewed', accepted: 'Accepted', declined: 'Declined', expired: 'Expired', revised: 'Revised',
};
const STATUS_OPTIONS = Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }));
const EDITABLE_STATUSES = ['draft', 'rejected'];

export default function QuotationsPage() {
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState(null);
  const [formTarget, setFormTarget] = useState(undefined); // undefined = closed, null = create, object = edit
  const [deleteTarget, setDeleteTarget] = useState(null);

  const debounced = useDebounce(search);
  const pages = useCursorPages(25);
  const options = useQuotationOptions();

  const params = useMemo(
    () => ({ ...pages.params, q: debounced || undefined, status: status || undefined }),
    [pages.params, debounced, status],
  );
  const { data, isLoading, error, refetch } = useQuotations(params);

  const create = useCreateQuotation();
  const update = useUpdateQuotation();
  const remove = useDeleteQuotation();

  const onFilter = (setter) => (value) => { setter(value); pages.reset(); };

  const columns = [
    {
      key: 'quote_no',
      header: 'Quotation',
      render: (q) => (
        <div>
          <div className="fw-600">
            {q.quote_no}
            {q.version_no > 1 && <Badge tone="neutral" className="ms-2">v{q.version_no}</Badge>}
          </div>
          <div className="t-xs text-muted">{q.title}</div>
        </div>
      ),
    },
    { key: 'account', header: 'Account', width: 180, render: (q) => q.account?.name ?? '—' },
    { key: 'status', header: 'Status', width: 150, render: (q) => <Badge tone={STATUS_TONE[q.status] ?? 'neutral'}>{STATUS_LABEL[q.status] ?? q.status}</Badge> },
    { key: 'total', header: 'Total', width: 120, align: 'end', render: (q) => money(q.totals?.grand_total, q.currency) },
    {
      key: 'discount', header: 'Discount', width: 100, align: 'end',
      render: (q) => <span className={q.totals?.discount_pct > 20 ? 'text-danger fw-600' : ''}>{percent(q.totals?.discount_pct)}</span>,
    },
    { key: 'valid_until', header: 'Valid until', width: 120, render: (q) => date(q.valid_until) },
    { key: 'owner', header: 'Owner', width: 150, render: (q) => q.owner?.full_name ?? '—' },
  ];

  return (
    <>
      <PageHeader title="Quotations" subtitle="Priced proposals, their approvals and their revisions.">
        {can('quotations', 'create') && (
          <button type="button" className="btn btn-add btn-sm-x" onClick={() => setFormTarget(null)}>
            <i className="bi bi-plus-lg me-1" aria-hidden="true" />New quotation
          </button>
        )}
      </PageHeader>

      <Card bodyClass="p-0">
        <div className="filter-bar">
          <SearchInput value={search} onChange={onFilter(setSearch)} placeholder="Search number, account or title…" className="flex-grow-1" />
          <ChipSelect label="Status" allLabel="All statuses" value={status} onChange={onFilter(setStatus)} options={STATUS_OPTIONS} />
        </div>

        <DataTable
          columns={columns}
          rows={data?.data ?? []}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          onRowClick={(q) => setOpenId(q.id)}
          page={data?.page}
          onNext={pages.goNext}
          onPrevious={pages.goPrevious}
          canGoPrevious={pages.canGoPrevious}
          emptyTitle="No quotations match these filters"
        />
      </Card>

      <QuotationDrawer
        id={openId}
        onClose={() => setOpenId(null)}
        onEdit={(q) => setFormTarget(q)}
        onDelete={(q) => setDeleteTarget(q)}
      />

      <QuotationFormModal
        open={formTarget !== undefined}
        quotation={formTarget}
        options={options}
        busy={create.isPending || update.isPending}
        onClose={() => setFormTarget(undefined)}
        onSubmit={(payload) => {
          if (formTarget) {
            update.mutate(
              { id: formTarget.id, patch: payload, rowVersion: formTarget.row_version },
              { onSuccess: () => setFormTarget(undefined) },
            );
          } else {
            create.mutate(payload, { onSuccess: () => setFormTarget(undefined) });
          }
        }}
      />

      <DeleteQuotationModal
        open={Boolean(deleteTarget)}
        quotation={deleteTarget}
        busy={remove.isPending}
        onClose={() => setDeleteTarget(null)}
        onConfirm={(reason) => remove.mutate(
          { id: deleteTarget.id, reason, rowVersion: deleteTarget.row_version },
          { onSuccess: () => { setDeleteTarget(null); setOpenId(null); } },
        )}
      />
    </>
  );
}

/**
 * The detail drawer: header totals, the lines, the revision lineage and the
 * approval history — plus whichever actions the quotation's current status
 * and the caller's permissions actually allow.
 */
function QuotationDrawer({ id, onClose, onEdit, onDelete }) {
  const { can, user } = useAuth();
  const [reviseOpen, setReviseOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [decideRequest, setDecideRequest] = useState(null);

  const { data: quote, isLoading } = useQuotation(id);
  const rootId = quote?.root_quotation_id ?? quote?.id;
  const { data: versions } = useQuotationVersions(rootId, Boolean(quote) && quote.version_no > 1);

  const revise = useReviseQuotation();
  const submit = useSubmitForApproval();
  const send = useSendQuotation();
  const decide = useDecideApproval();

  if (!id) return null;

  const editable = quote && EDITABLE_STATUSES.includes(quote.status);
  const myPending = quote?.approval?.requests?.find((r) => r.status === 'pending' && (!r.assigned_to || r.assigned_to.id === user?.id));

  return (
    <>
      <Drawer
        open={Boolean(id)}
        onClose={onClose}
        title={quote?.quote_no ?? 'Quotation'}
        subtitle={quote?.title}
        width={640}
        footer={quote ? (
          <>
            {editable && can('quotations', 'delete') && (
              <button type="button" className="btn-x btn-ghost me-auto" onClick={() => onDelete(quote)}>Delete</button>
            )}
            {editable && can('quotations', 'edit') && (
              <button type="button" className="btn-x btn-ghost" onClick={() => onEdit(quote)}>Edit</button>
            )}
            {editable && can('quotations', 'edit') && (
              <button
                type="button" className="btn-x btn-ghost" disabled={submit.isPending}
                onClick={() => submit.mutate({ id: quote.id, payload: {} })}
              >
                {submit.isPending ? 'Submitting…' : 'Submit for approval'}
              </button>
            )}
            {!editable && quote.is_current && can('quotations', 'create') && (
              <button type="button" className="btn-x btn-ghost" onClick={() => setReviseOpen(true)}>Revise</button>
            )}
            {quote.status === 'approved' && can('quotations', 'send') && (
              <button type="button" className="btn-x btn-primary-x" onClick={() => setSendOpen(true)}>Send</button>
            )}
            {myPending && can('quotations', 'approve') && (
              <button type="button" className="btn-x btn-primary-x" onClick={() => setDecideRequest(myPending)}>Decide</button>
            )}
          </>
        ) : null}
      >
        {isLoading || !quote ? (
          <Spinner />
        ) : (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Badge tone={STATUS_TONE[quote.status] ?? 'neutral'}>{STATUS_LABEL[quote.status] ?? quote.status}</Badge>
              {quote.version_no > 1 && <Badge tone="neutral">Version {quote.version_no}</Badge>}
              {quote.totals.discount_pct > 20 && <Badge tone="rose">Discount above 20%</Badge>}
              {!quote.is_current && <Badge tone="neutral">Superseded</Badge>}
            </div>

            <dl className="detail-grid mb-3">
              <div className="detail-row"><dt>Account</dt><dd>{quote.account?.name ?? '—'}</dd></div>
              <div className="detail-row"><dt>Opportunity</dt><dd>{quote.opportunity?.name ?? '—'}</dd></div>
              <div className="detail-row"><dt>Owner</dt><dd>{quote.owner?.full_name ?? '—'}</dd></div>
              <div className="detail-row"><dt>Created</dt><dd>{date(quote.created_at)}</dd></div>
              <div className="detail-row"><dt>Valid until</dt><dd>{date(quote.valid_until)}</dd></div>
              <div className="detail-row"><dt>Payment terms</dt><dd>{quote.payment_terms ?? '—'}</dd></div>
            </dl>

            <h3 className="card-x-title mb-2">Lines</h3>
            <div className="table-x-wrap mb-3">
              <table className="table-x is-dense">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="text-end">Qty</th>
                    <th className="text-end">Unit</th>
                    <th className="text-end">Disc.</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(quote.items ?? []).map((line) => (
                    <tr key={line.id}>
                      <td>{line.description}</td>
                      <td className="text-end">{line.quantity}</td>
                      <td className="text-end">{money(line.unit_price, quote.currency)}</td>
                      <td className="text-end">{percent(line.discount_pct)}</td>
                      <td className="text-end fw-600">{money(line.line_total, quote.currency)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={4} className="text-end">Subtotal</td><td className="text-end">{money(quote.totals.subtotal, quote.currency)}</td></tr>
                  {quote.totals.discount_amount > 0 && (
                    <tr><td colSpan={4} className="text-end">Discount</td><td className="text-end">−{money(quote.totals.discount_amount, quote.currency)}</td></tr>
                  )}
                  {quote.totals.tax_amount > 0 && (
                    <tr><td colSpan={4} className="text-end">Tax</td><td className="text-end">{money(quote.totals.tax_amount, quote.currency)}</td></tr>
                  )}
                  {quote.totals.shipping_amount > 0 && (
                    <tr><td colSpan={4} className="text-end">Shipping</td><td className="text-end">{money(quote.totals.shipping_amount, quote.currency)}</td></tr>
                  )}
                  <tr><td colSpan={4} className="text-end fw-700">Grand total</td><td className="text-end fw-700">{money(quote.totals.grand_total, quote.currency)}</td></tr>
                </tfoot>
              </table>
            </div>

            {quote.approval?.requests?.length > 0 && (
              <>
                <h3 className="card-x-title mb-2">Approval history</h3>
                <ul className="list-unstyled t-sm mb-3">
                  {quote.approval.requests.map((r) => (
                    <li key={r.id} className="d-flex align-items-center gap-2 mb-1">
                      <Badge tone={r.status === 'approved' ? 'emerald' : r.status === 'rejected' ? 'rose' : 'amber'}>
                        Step {r.step_no}: {r.status}
                      </Badge>
                      <span className="ink-3">{r.assigned_to?.full_name ?? 'Unassigned'}{r.decided_at ? ` · ${date(r.decided_at)}` : ''}</span>
                      {r.decision_note && <span className="ink-4 fst-italic">— {r.decision_note}</span>}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {quote.approval.rejection_reason && (
              <div className="alert-x alert-danger-x mb-3" role="alert">Rejected: {quote.approval.rejection_reason}</div>
            )}

            {versions?.data?.length > 1 && (
              <>
                <h3 className="card-x-title mb-2">Revision history</h3>
                <ul className="list-unstyled t-sm mb-0">
                  {versions.data.map((v) => (
                    <li key={v.id} className={`mb-1 ${v.id === quote.id ? 'fw-600' : ''}`}>
                      v{v.version_no} — {STATUS_LABEL[v.status] ?? v.status} · {date(v.created_at)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </Drawer>

      <ReviseQuotationModal
        open={reviseOpen}
        quotation={quote}
        busy={revise.isPending}
        onClose={() => setReviseOpen(false)}
        onConfirm={(payload) => revise.mutate({ id: quote.id, payload }, { onSuccess: () => setReviseOpen(false) })}
      />
      <SendQuotationModal
        open={sendOpen}
        quotation={quote}
        busy={send.isPending}
        onClose={() => setSendOpen(false)}
        onConfirm={(payload) => send.mutate({ id: quote.id, payload }, { onSuccess: () => setSendOpen(false) })}
      />
      <DecideApprovalModal
        open={Boolean(decideRequest)}
        request={decideRequest ? { ...decideRequest, entity: { label: `${quote?.quote_no} — ${quote?.title ?? ''}` } } : null}
        busy={decide.isPending}
        onClose={() => setDecideRequest(null)}
        onConfirm={(payload) => decide.mutate(
          { requestId: decideRequest.id, payload },
          { onSuccess: () => setDecideRequest(null) },
        )}
      />
    </>
  );
}
